import { createUserWithEmailAndPassword } from 'firebase/auth'
import { firebaseAuth } from '../../../api/FirebaseAuth'
import { makeAutoObservable } from 'mobx'
import AuthService from './../../../shared/services/Auth.service'
import AdminApi from './../../../api/admin-api/admin-api'
import UserProfileService from './../../../shared/services/UserProfile.service'
import UsertypeService from './../../../shared/services/Usertype.service'
import UserService from './../../../shared/services/User.service'
import getRandomPassword from './../../../components/shared/GeneratePassword'
import SubscriptionService from './../../../shared/services/Subscription.service'
import StripeSubscriptionService from '../../../shared/services/StripeSubscription.service'
import StripeCustomerService from '../../../shared/services/StripeCustomer.service'
import StorageService from './../../../shared/services/Storage.service'
import { getIp } from './../../../shared/utilities/getIp.function'
import { TermSetFactory, TermFactory, RecruitingLeadSource } from './../../../shared/factories'
import moment from 'moment'
import { toast } from 'react-toastify'
import completeEnrollmentAudio from '../assets/complete-enrollment.mp3'
import { commissionLevels } from '../../../constants/commisionLevels'

const USE_RANDOM_PASS = true

const getLeaderByUplineId = async userId =>
  new Promise((resolve, reject) =>
    AdminApi.getLimitedLeadersByUplineId(userId).then(leaders =>
      resolve(
        (leaders?.data?.data || []).sort((a, b) =>
          `${[a.u_fname, a.u_lname].join(' ').trim()}`.localeCompare(`${[b.u_fname, b.u_lname].join(' ').trim()}`, 'en')
        )
      )
    )
  )

const getDownlineLeaderById = async userId =>
  new Promise((resolve, reject) =>
    AdminApi.getLimitedDownlineLeadersId(userId).then(recuritingLeaders =>
      resolve(
        (recuritingLeaders?.data?.data || []).sort((a, b) =>
          `${[a.u_fname, a.u_lname].join(' ').trim()}`.localeCompare(`${[b.u_fname, b.u_lname].join(' ').trim()}`, 'en')
        )
      )
    )
  )

const generatePassword = () =>
  USE_RANDOM_PASS
    ? getRandomPassword.getRandomPassword(8, {
        numbers: true,
        alphabets: true,
        specialCharacters: true,
        upperCase: true,
      })
    : ''

const isAuthenticated = () => UserProfileService.getUserId() > 0

const getProfileUserId = () => UserProfileService.getUserId()

const isAssumed = () => isAuthenticated() && UserProfileService.isAssumed()

const isRecruiter = usertypeId =>
  usertypeId
    ? UsertypeService.isA('recruiter-group', usertypeId) || UsertypeService.isA('internal-admin', usertypeId)
    : isAuthenticated() && UserProfileService.isA(['recruiter-group', 'internal-admin'])

const isEnrolled = () => parseInt(UserProfileService.get('completed_enroll')) === 1

const getInitialState = () => {
  if (isAuthenticated() && isAssumed() && !isEnrolled()) return 'IntakeFormPackageSelection'
  if (isAuthenticated() && !isRecruiter() && !isEnrolled()) return 'IntakeFormPackageSelection'

  return 'IntakeFormIdentity'
}

class EnrollmentStore {
  // Enrolled/Enrolling User critical props.
  firebaseUid = null
  userId = null
  userIp = null
  usertypeId = 111
  newUserID = null
  recruiterId = null
  uplineId = null
  commissionCode = null

  // Intake Form Stages.
  intakeStage = null
  nextIntakeStage = null

  // Boolean flag indicating if in async process to create/enroll acct.
  isCreating = false
  isCompleting = false
  isLoading = false
  isCheckingHasCharged = false
  // Boolean flag indicating if in async process for payment completed successfully.
  hasPaid = false

  // URL flag: when true, Affiliate Agent package is visible in package selection.
  showAffiliate = false

  // Intake Form values for acct creation && validity.
  intake = {
    term_set: [],
  }
  termIds = []
  termSetId = null
  validity = {}

  // Recruiter info
  upline = {}
  leaders = [] // If the enrolling user is a team_recruiter, they can select from this list.
  recuritingLeaders = []

  // Intake Form/Enrolling Dependencies.
  TermSets = {}
  commissionLevels = []

  // Billing/subscription data.
  subscriptionData = false

  terms = []
  termSet = []
  original_commission_id = null

  constructor() {
    makeAutoObservable(this)
    this.intakeStage = getInitialState()
  }

  setIntakeStage = stage => {
    this.intakeStage = stage
  }

  setNextIntakeStage = stage => {
    this.nextIntakeStage = stage
  }

  setIp = async () => {
    if (!isAssumed()) getIp().then(ip => (this.userIp = ip))
  }

  reset = () => {
    this.intake = {
      term_set: [],

      // Set Default Commission Type to Agent.
      commission_id: '209',

      // If enabled, set a random password for the agent.
      u_password: USE_RANDOM_PASS ? generatePassword() : '',
    }

    this.usertypeId = 111
    this.intakeStage = getInitialState()
  }

  isAuthenticated = () => this.userId > 0 || isAuthenticated()

  isRecruiter = usertypeId => isRecruiter(usertypeId)

  isAssumed = () => isAssumed()

  getProfileUserId = () => getProfileUserId()

  isEnrolled = () => isAuthenticated() && parseInt(UserProfileService.get('completed_enroll')) === 1

  isValid = fields => fields.every(field => !!this.validity[field]?.isValid)

  isDownlineRecruiter = () =>
    this.isAuthenticated() && this.isAssumed() && this.isRecruiter(this.getAssumed()?.usertype_id)

  getAssumed = () => {
    if (this.isAssumed()) return UserProfileService.getUserDetails()
    return
  }

  setUpline = async ulogin => {
    const fetchUplineUser = async () => {
      try {
        return (await AdminApi.getUserDataByULogin(ulogin))?.data
      } catch (ex) {
        console.log(ex)
        return false
      }
    }

    const uplineData = await fetchUplineUser(),
      upline = uplineData && uplineData?.data
    if (upline && typeof upline === 'object') {
      if (!upline?.can_recruit && !upline?.has_agency_builder_access) return 'UPLINE_CANNOT_RECRUIT'
      if (upline?.lock_recruit) return 'UPLINE_LOCKED_RECRUIT'

      this.upline = {
        id: upline?.id,
        u_fname: upline?.u_fname,
        u_lname: upline?.u_lname,
        u_nickname: upline?.u_nickname,
        u_marketing_title: upline?.u_marketing_title,
        u_marketing_phone: upline?.marketing_phone,
        u_picture: upline?.u_picture,
        image_url: uplineData?.imageUrl,
        is_team_recruiter: upline?.is_team_recruiter,
        is_admin_recruiter: upline?.is_admin_recruiter,
        team_logo: upline?.team_logo,
        show_white_label: upline?.show_white_label,
        is_white_label: upline?.is_white_label,
        usertype_id: upline?.usertype_id,
        commission_id: upline?.commission_id,
      }
      // fetch the downline leaders, starting with upline, that can be used for team assignment.
      if ((upline?.is_team_recruiter || upline?.is_admin_recruiter) && upline?.u_upline_id) {
        getLeaderByUplineId(upline.u_upline_id).then(leaders => (this.leaders = leaders))

        // Replace commission id with leader's for admins
        this.upline.commission_id = upline?.leader_commission_id || upline?.commission_id
        this.original_commission_id = upline?.leader_commission_id || upline?.commission_id
      }

      return true
    }

    return 'INVALID_UPLINE_USER'
  }

  setDownline = async ulogin => {
    const fetchUser = async () => {
      try {
        return (await AdminApi.getUserDataByULogin(ulogin))?.data
      } catch (ex) {
        console.log(ex)
        return false
      }
    }

    const userData = await fetchUser()
    const user = userData && userData?.data
    if (user && typeof user === 'object') {
      getDownlineLeaderById(user.id).then(recuritingLeaders => (this.recuritingLeaders = recuritingLeaders))
    }
  }

  setCommissionLevelsByUpline = async upline_id => {
    const upline = (await AdminApi.getUserDataById(upline_id))?.data
    this.upline.commission_id = upline?.data?.commission_id
  }

  fetchRecruitingLeadSources = async () => {
    const fetch = async () => {
      try {
        return await RecruitingLeadSource.search({
          search: {},
          pagination: false,
        })
      } catch (ex) {
        console.error('Failed to load enrollment terms.', ex)
      }
      return []
    }

    const Response = await Promise.all([fetch()])

    let Last = null,
      RecruitingLeadSources = Response.shift()
    RecruitingLeadSources = (RecruitingLeadSources || [])
      .sort((a, b) => `${a?.get('ls_name') || ''}`.localeCompare(`${b?.get('ls_name') || ''}`, 'en'))
      .filter(a => {
        if (a?.id() && parseInt(a.id()) === 1) {
          Last = a
          return false
        }
        return true
      })
    if (Last) RecruitingLeadSources.push(Last)

    this.RecruitingLeadSources = RecruitingLeadSources
  }

  fetchTerms = async () => {
    const fetchTermDocs = async () => {
      try {
        return await TermFactory.search({
          search: { is_enroll: 1, active: 1 },
          pagination: false,
        })
      } catch (ex) {
        console.error('Failed to load enrollment terms.', ex)
      }
    }

    const fetchTermSets = async () => {
      try {
        return await TermSetFactory.search({
          search: { usertype_id: [91] },
          pagination: false,
        })
      } catch (ex) {
        console.error('Failed to load enrollment term sets.', ex)
      }
    }

    const Response = await Promise.all([fetchTermDocs(), fetchTermSets()]),
      Terms = Response.shift(),
      TermSets = (Response.shift() || []).filter((TermSet, idx) => idx === 0)

    TermSets.forEach(TermSet => {
      TermSet.parseTermStmt({ Terms })
      this.TermSets[TermSet.get('usertype_id')] = TermSet
    })
  }

  fetchCommissionLevels = async () => {
    let commissionLevels = []
    try {
      commissionLevels = (await AdminApi.getCommissionLevelList())?.data?.data
    } catch (ex) {}

    this.commissionLevels = commissionLevels
  }

  enroll = async verify => {
    // New Agent Enrollment Payload from Intake form
    const enrollmentPayload = {
      u_fname: this.intake.u_fname.trim(),
      u_lname: this.intake.u_lname.trim(),
      u_email: this.intake.u_email.trim(),
      u_password: this.intake.u_password,
      u_address1: this.intake.u_address1,
      u_city: this.intake.u_city,
      u_zip: this.intake.u_zip,
      u_phone: this.intake.u_phone.trim(),
      u_birthday: moment(this.intake.u_birthday).format('YYYY-MM-DD'),
      u_state: this.intake.u_state,
      commission_id: this.intake.commission_id,
      usertype_id: 111,
      // record whether the Affiliate package was actually offered on
      // this link. The account is born as 111 before package selection, so the
      // welcome email needs this to avoid mailing the Affiliate template to a
      // recruit who was only ever shown Signature.
      show_affiliate: this.showAffiliate ? 1 : 0,
      u_enroll_source: this.intake.u_enroll_source,
      u_enroll_source_detail: this.intake.u_enroll_source_detail,
      u_upline_id: this.upline.id,
      upline_override: this.intake.upline_override,
      recuriter_override: this.recruiterId ? this.recruiterId : this.intake.recuriter_override,
      npn: this.intake.npn.trim(),
    }

    if (verify === true)
      await UserService.enroll(this.upline.id, enrollmentPayload, {
        verify: true,
      })

    const getFirebaseUid = async () => {
      if (this.firebaseUid) return this.firebaseUid

      let firebaseUser = await this.registerFirebaseUser(this.intake.u_email, this.intake.u_password)

      if (firebaseUser && firebaseUser?.user?.uid) {
        this.firebaseUid = firebaseUser?.user?.uid
        return firebaseUser.user.uid
      }
    }

    // create Firebase User
    const fUid = await getFirebaseUid()

    // Here we will now actually create user in USABG
    return (
      (await UserService.enroll(this.upline.id, {
        ...enrollmentPayload,
        firebase_uid: fUid,
      })) || false
    )
  }

  registerFirebaseUser = async (email, password) => await createUserWithEmailAndPassword(firebaseAuth, email, password)

  authenticateNewUser = async (email, password) => {
    let result
    try {
      result = await AuthService.login(email, password)
      this.userId = UserProfileService.getUserId()
    } catch (ex) {
      result = `${ex}`
    }

    if (result && !isNaN(result?.usertype_id)) return this.userId > 0
    else return false

    // throw Error(
    //   result && typeof result === 'string'
    //     ? result
    //     : 'Unable to login new account.  Please check your email for login details.  Contact support if errors persist.'
    // )
  }

  authenticateDownlineUser = async userId => {
    try {
      await AuthService.assumeUser(userId)
      this.userId = userId
      return this.userId > 0
    } catch (error) {
      return false
    }
  }

  authenticate = async newUser => {
    if (newUser?.id && newUser.id === newUser?.auth_id) {
      // login to own account.
      return this.authenticateNewUser(this.intake.u_email, this.intake.u_password)
    } else if (newUser?.id && newUser?.auth_id) {
      // login to downline account.
      return this.authenticateDownlineUser(newUser.id)
    }

    // advance stage.
    // no longer permitted to go back.
    return false
  }

  completeEnrollment = async usertypeId => {
    this.isCompleting = true

    if (!this.newUserID?.id) {
      const userId = await UserProfileService.getUserId()
      if (userId) {
        // Check if the userId is valid.
        console.log(`Agent enrollment using the UserProfileService ID ${userId}`)
        this.newUserID = this.newUserID || {} // If this.newUserID is null or undefined, initialize it as an empty object.
        this.newUserID.id = userId // Assign the fetched user ID to the newUserID property.
      }
    }

    console.log(`Agent ${this.newUserID?.id} enrollment started for userType=${usertypeId}`)

    if (!this.userIp && !isAssumed()) this.userIp = await getIp()

    return new Promise((resolve, reject) => {
      // Get the new user id which got created from enroll page
      // The old logic of fetching user ID from localstorage is replaced
      const userId = this.newUserID?.id
      if (!userId) {
        console.log(userId)
        throw new Error(`Unable to identify the new enroll agent ID!`)
      }
      UserService.complete(userId, {
        usertypeId: this.usertypeId,
        termset_id: this.intake.termset_id,
        termset_signature: this.intake.term_set_signature,
        term_ids: this.intake.term_ids,
        subscription_id: this.subscriptionId,
        u_password: this.intake.u_password,
        ...(this.userIp && !isAssumed() ? { accepted_ip: this.userIp } : {}),
      }).then(
        res => {
          // Is sucess then only set completed enrollment
          UserProfileService.set('completed_enroll', 1)
          UserProfileService.set('usertype_id', usertypeId)
          console.log(`Agent ${this.newUserID?.id} enrollment completed for userType=${usertypeId}`)
          if (UserProfileService.isAssumed()) StorageService.set('prev_url', '/dashboard')

          this.isCompleting = false
          this.reset()
          resolve(true)
        },
        err => {
          console.log(`Agent ${this.newUserID?.id} enrollment failed for userType=${usertypeId}`)
          this.isCompleting = false
          reject(err)
        }
      )
    })
  }

  initiateSubscription = async () => {
    if (!this.userIp && !isAssumed()) this.userIp = await getIp()

    try {
      await SubscriptionService.initiateSubscription({
        userId: this.userId || UserProfileService.getUserId(),
        usertypeId: this.usertypeId || 91,
        subClass: 'sig_agent',
        ...(this.userIp && !isAssumed() ? { userIp: this.userIp } : {}),
      })
    } catch (ex) {}

    if (SubscriptionService.getSubscriptionData()?.subscription_id)
      this.subscriptionData = SubscriptionService.getSubscriptionData()
  }

  getTermIds = usertypeId => {
    const TermSet = this.TermSets[usertypeId]
    if (TermSet) {
      return TermSet?.termIds
    }
    return []
  }

  getTermSetId = usertypeId => {
    const TermSet = this.TermSets[usertypeId]
    if (TermSet) {
      return TermSet?.id()
    }
    return null
  }

  getSubscirptions = async () => {
    this.isCheckingHasCharged = true

    try {
      const customer = (
        (
          await StripeCustomerService.search({
            pagination: false,
            search: { user_id: UserProfileService.getUserId() },
          })
        )?.models || []
      ).shift()

      if (customer?.id) {
        const subscriptions =
          (
            await StripeSubscriptionService.search({
              pagination: false,
              search: { customer_id: customer.id },
              order_by: { started_at: 'DESC' },
            })
          )?.models || []

        if (Array.isArray(subscriptions) && subscriptions.length >= 1 && subscriptions[0].sub_status === 'active') {
          this.usertypeId = 91
          this.subscriptionId = subscriptions[0].id
          this.intake.term_ids = this.getTermIds(91)
          this.intake.termset_id = this.getTermSetId(91)
          this.intake.term_set_signature = [UserProfileService.get('u_fname'), UserProfileService.get('u_lname')].join(
            ' '
          )

          this.completeEnrollment(91).then(
            async res => {
              try {
                this.isCheckingHasCharged = false
                const enrollmentAudio = new Audio(completeEnrollmentAudio)
                enrollmentAudio.play()
                enrollmentAudio.onended = () => {
                  window.location = '/welcome'
                }
              } catch (err) {
                this.isCheckingHasCharged = false
                window.location = '/welcome'
              }
            },
            err => {
              this.isCheckingHasCharged = false
              toast.error(`${err}`, { position: toast.POSITION.TOP_RIGHT })
            }
          )
          return
        }
      }
    } catch (err) {
      console.error('getSubscirptions error:', err)
    }

    this.isCheckingHasCharged = false
  }
}

export default new EnrollmentStore()
